import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import GroupIcon from '@mui/icons-material/Group';
import SearchIcon from '@mui/icons-material/Search';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMemo, useRef, useState } from 'react';

import type { AdminApp, AppUserRow } from '../../api/queries';
import {
  type DirectoryUser,
  useAdminApps,
  useAppUsers,
  useBulkGrant,
  useCreateApp,
  useDeleteApp,
  useDirectory,
  useInUseBulk,
  usePatchApp,
} from '../../api/queries';
import { usd } from '../../lib/format';

export default function AdminApps() {
  const { data, isLoading, error } = useAdminApps();
  const bulkSave = useInUseBulk();
  const deleteApp = useDeleteApp();
  const patchApp = usePatchApp();
  const { enqueueSnackbar } = useSnackbar();

  const toggleFlag = async (name: string, field: 'hidden' | 'site_visible', checked: boolean) => {
    try {
      await patchApp.mutateAsync({ name, patch: { [field]: checked } });
    } catch (err) {
      enqueueSnackbar(`Update failed: ${err instanceof Error ? err.message : err}`, {
        variant: 'error',
      });
    }
  };

  const [search, setSearch] = useState('');
  const [visibility, setVisibility] = useState<'all' | 'in_use' | 'off'>('in_use');
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const [editing, setEditing] = useState<AdminApp | null>(null);
  const [creating, setCreating] = useState(false);
  const [usersFor, setUsersFor] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [teamFilter, setTeamFilter] = useState('');

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data ?? []).filter((a) => {
      if (visibility === 'in_use' && !a.in_use) return false;
      if (visibility === 'off' && a.in_use) return false;
      if (categoryFilter && a.category !== categoryFilter) return false;
      if (teamFilter && (a.team ?? a.primary_team) !== teamFilter) return false;
      return (
        !q ||
        `${a.display_name ?? ''} ${a.app_name} ${a.vendor ?? ''} ${a.owner_name ?? ''}`
          .toLowerCase()
          .includes(q)
      );
    });
  }, [data, search, visibility, categoryFilter, teamFilter]);

  const categoryOptions = useMemo(
    () => [...new Set((data ?? []).map((a) => a.category).filter(Boolean))].sort() as string[],
    [data],
  );
  const teamOptions = useMemo(
    () =>
      [
        ...new Set((data ?? []).map((a) => a.team ?? a.primary_team).filter(Boolean)),
      ].sort() as string[],
    [data],
  );

  if (isLoading) return <CircularProgress />;
  if (error) return <Alert severity="error">Failed to load apps: {String(error)}</Alert>;

  const pendingCount = Object.keys(pending).length;

  const saveInUse = async () => {
    const updates = Object.entries(pending).map(([app_name, in_use]) => ({ app_name, in_use }));
    try {
      await bulkSave.mutateAsync(updates);
      setPending({});
      enqueueSnackbar(`Saved ${updates.length} in-use changes`, { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(`Save failed: ${err instanceof Error ? err.message : err}`, {
        variant: 'error',
      });
    }
  };

  const remove = async (name: string) => {
    if (!window.confirm(`Soft-delete ${name}? It disappears from the catalog (recoverable).`))
      return;
    try {
      await deleteApp.mutateAsync(name);
      enqueueSnackbar(`${name} deleted`, { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(`Delete failed: ${err instanceof Error ? err.message : err}`, {
        variant: 'error',
      });
    }
  };

  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        spacing={2}
        sx={{ alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
          <TextField
            placeholder="Filter by app or vendor…"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
          <ToggleButtonGroup
            size="small"
            exclusive
            value={visibility}
            onChange={(_, v) => v && setVisibility(v)}
          >
            <ToggleButton value="in_use">In use</ToggleButton>
            <ToggleButton value="off">Off</ToggleButton>
            <ToggleButton value="all">All</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            select
            size="small"
            label="Category"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">All categories</MenuItem>
            {categoryOptions.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Team"
            value={teamFilter}
            onChange={(e) => setTeamFilter(e.target.value)}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">All teams</MenuItem>
            {teamOptions.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <Stack direction="row" spacing={1}>
          {pendingCount > 0 && (
            <Button variant="contained" onClick={saveInUse} disabled={bulkSave.isPending}>
              Save {pendingCount} in-use change{pendingCount > 1 ? 's' : ''}
            </Button>
          )}
          <Button startIcon={<AddIcon />} variant="outlined" onClick={() => setCreating(true)}>
            Add app
          </Button>
        </Stack>
      </Stack>

      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>App</TableCell>
            <TableCell>Category</TableCell>
            <TableCell>Vendor</TableCell>
            <TableCell>Owner</TableCell>
            <TableCell>Team</TableCell>
            <TableCell align="right">Users</TableCell>
            <TableCell align="right">Matrix $</TableCell>
            <TableCell align="right">Actual $</TableCell>
            <TableCell align="center">In use</TableCell>
            <TableCell align="center">Universal</TableCell>
            <TableCell align="center">Hidden</TableCell>
            <TableCell align="center">Site</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {visible.map((a) => {
            const inUse = pending[a.app_name] ?? a.in_use;
            const overridden = Object.keys(a.admin_overrides ?? {});
            return (
              <TableRow
                key={a.app_name}
                hover
                onDoubleClick={() => setEditing(a)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <span>{a.display_name ?? a.app_name}</span>
                    {a.display_name && a.display_name !== a.app_name && (
                      <Typography variant="caption" color="text.secondary">
                        ({a.app_name})
                      </Typography>
                    )}
                    {overridden.length > 0 && (
                      <Tooltip title={`Admin-pinned: ${overridden.join(', ')} (sync won't touch)`}>
                        <Chip size="small" variant="outlined" label="pinned" />
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
                <TableCell>{a.category ?? '—'}</TableCell>
                <TableCell>{a.vendor ?? '—'}</TableCell>
                <TableCell>{a.owner_name ?? '—'}</TableCell>
                <TableCell>{a.team ?? a.primary_team ?? '—'}</TableCell>
                <TableCell align="right">{a.account_count || a.users_count || 0}</TableCell>
                <TableCell align="right">{usd(a.cost_matrix_usd)}</TableCell>
                <TableCell align="right">{usd(a.cost_actual_usd)}</TableCell>
                <TableCell align="center">
                  <Switch
                    size="small"
                    slotProps={{ input: { 'aria-label': `in-use ${a.app_name}` } }}
                    checked={inUse}
                    color={pending[a.app_name] !== undefined ? 'warning' : 'primary'}
                    onChange={(e) =>
                      setPending((p) => {
                        const next = { ...p };
                        if (e.target.checked === a.in_use) delete next[a.app_name];
                        else next[a.app_name] = e.target.checked;
                        return next;
                      })
                    }
                  />
                </TableCell>
                <TableCell align="center">{a.is_universal ? '✓' : ''}</TableCell>
                <TableCell align="center">
                  <Tooltip title="Hidden from the employee catalog (cost still counts)">
                    <Switch
                      size="small"
                      slotProps={{ input: { 'aria-label': `hidden ${a.app_name}` } }}
                      checked={!!a.hidden}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => toggleFlag(a.app_name, 'hidden', e.target.checked)}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Available to site crews (DC-floor / Drumheller curated view)">
                    <Switch
                      size="small"
                      color="success"
                      slotProps={{ input: { 'aria-label': `site ${a.app_name}` } }}
                      checked={!!a.site_visible}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => toggleFlag(a.app_name, 'site_visible', e.target.checked)}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => setUsersFor(a.app_name)}
                    title="Who has access"
                  >
                    <GroupIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setEditing(a)} title="Edit">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => remove(a.app_name)} title="Delete">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {editing && (
        <AppEditDialog
          app={editing}
          categories={categoryOptions}
          teams={teamOptions}
          onClose={() => setEditing(null)}
        />
      )}
      {creating && (
        <AppCreateDialog categories={categoryOptions} onClose={() => setCreating(false)} />
      )}
      {usersFor && <AppUsersDialog appName={usersFor} onClose={() => setUsersFor(null)} />}
    </Stack>
  );
}

// ── Edit dialog ─────────────────────────────────────────────────────
const EDIT_FIELDS: { key: string; label: string; numeric?: boolean; multiline?: boolean }[] = [
  { key: 'display_name', label: 'App name (display — the original name stays the internal key)' },
  { key: 'description', label: 'Description', multiline: true },
  { key: 'category', label: 'Category' },
  { key: 'vendor', label: 'Vendor' },
  { key: 'team', label: 'Team' },
  { key: 'owner_name', label: 'Owner name' },
  { key: 'owner_email', label: 'Owner email' },
  { key: 'homepage_url', label: 'Homepage URL' },
  { key: 'login_url', label: 'Login URL' },
  { key: 'users_count', label: 'Users count (manual for non-SSO apps)', numeric: true },
  { key: 'cost_matrix_usd', label: 'Cost matrix (USD/yr)', numeric: true },
  { key: 'cost_actual_usd', label: 'Cost actual (USD/yr)', numeric: true },
  { key: 'notes', label: 'Notes', multiline: true },
];

function AppEditDialog({
  app,
  categories,
  teams,
  onClose,
}: {
  app: AdminApp;
  categories: string[];
  teams: string[];
  onClose: () => void;
}) {
  const patchApp = usePatchApp();
  const { data: directory } = useDirectory();
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      EDIT_FIELDS.map((f) => [
        f.key,
        String((app as unknown as Record<string, unknown>)[f.key] ?? ''),
      ]),
    ),
  );
  const [isUniversal, setIsUniversal] = useState(!!app.is_universal);
  const [requiresApproval, setRequiresApproval] = useState(!!app.requires_approval);
  const [hidden, setHidden] = useState(!!app.hidden);
  const [siteVisible, setSiteVisible] = useState(!!app.site_visible);

  const save = async () => {
    const patch: Record<string, unknown> = {};
    for (const f of EDIT_FIELDS) {
      const raw = form[f.key].trim();
      const orig = (app as unknown as Record<string, unknown>)[f.key];
      const value = raw === '' ? null : f.numeric ? Number(raw) : raw;
      if (value !== (orig ?? null)) patch[f.key] = value;
    }
    if (isUniversal !== !!app.is_universal) patch.is_universal = isUniversal;
    if (requiresApproval !== !!app.requires_approval) patch.requires_approval = requiresApproval;
    if (hidden !== !!app.hidden) patch.hidden = hidden;
    if (siteVisible !== !!app.site_visible) patch.site_visible = siteVisible;
    if (Object.keys(patch).length === 0) return onClose();
    try {
      await patchApp.mutateAsync({ name: app.app_name, patch });
      enqueueSnackbar(`${app.app_name} updated`, { variant: 'success' });
      onClose();
    } catch (err) {
      enqueueSnackbar(`Update failed: ${err instanceof Error ? err.message : err}`, {
        variant: 'error',
      });
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit — {app.app_name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} className="pt-2">
          {EDIT_FIELDS.map((f) =>
            f.key === 'owner_name' ? (
              <Autocomplete<DirectoryUser | string, false, false, true>
                key={f.key}
                freeSolo
                size="small"
                options={directory ?? []}
                value={form.owner_name}
                getOptionLabel={(o) => (typeof o === 'string' ? o : `${o.name ?? o.email}`)}
                isOptionEqualToValue={(o, v) =>
                  (typeof o === 'string' ? o : o.email) === (typeof v === 'string' ? v : v.email)
                }
                renderOption={(props, o) =>
                  typeof o === 'string' ? (
                    <li {...props} key={o}>
                      {o}
                    </li>
                  ) : (
                    <li {...props} key={o.email}>
                      {o.name ?? o.email}
                      {o.name ? ` — ${o.email}` : ''}
                    </li>
                  )
                }
                onChange={(_e, v) => {
                  if (v && typeof v !== 'string') {
                    setForm((s2) => ({
                      ...s2,
                      owner_name: v.name ?? v.email,
                      owner_email: v.email,
                    }));
                  } else {
                    setForm((s2) => ({ ...s2, owner_name: (v as string) ?? '' }));
                  }
                }}
                onInputChange={(_e, v, reason) => {
                  if (reason === 'input') setForm((s2) => ({ ...s2, owner_name: v }));
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Owner (pick an active user)" />
                )}
              />
            ) : f.key === 'category' || f.key === 'team' ? (
              <Autocomplete
                key={f.key}
                freeSolo
                size="small"
                options={f.key === 'category' ? categories : teams}
                value={form[f.key]}
                onInputChange={(_e, v) => setForm((s) => ({ ...s, [f.key]: v }))}
                renderInput={(params) => <TextField {...params} label={f.label} />}
              />
            ) : (
              <TextField
                key={f.key}
                label={f.label}
                size="small"
                type={f.numeric ? 'number' : 'text'}
                value={form[f.key]}
                onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                multiline={f.multiline}
              />
            ),
          )}
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Switch checked={isUniversal} onChange={(e) => setIsUniversal(e.target.checked)} />
            <Typography variant="body2">
              Universal — every HR-active employee has this automatically
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Switch
              checked={requiresApproval}
              onChange={(e) => setRequiresApproval(e.target.checked)}
            />
            <Typography variant="body2">
              Requires approval — critical system or per-seat license
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Switch checked={hidden} onChange={(e) => setHidden(e.target.checked)} />
            <Typography variant="body2">
              Hidden — removed from the employee catalog (cost still counts; unaffected by in-use)
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Switch checked={siteVisible} onChange={(e) => setSiteVisible(e.target.checked)} />
            <Typography variant="body2">
              Available to site crews — shows in the curated view for DC-floor / site roles
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={patchApp.isPending}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Create dialog ───────────────────────────────────────────────────
function AppCreateDialog({
  categories,
  onClose,
}: {
  categories: string[];
  onClose: () => void;
}) {
  const createApp = useCreateApp();
  const { enqueueSnackbar } = useSnackbar();
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');

  const save = async () => {
    if (!name.trim()) return;
    try {
      await createApp.mutateAsync({
        app_name: name.trim(),
        category: category.trim() || null,
        in_use: true,
      });
      enqueueSnackbar(`${name.trim()} created`, { variant: 'success' });
      onClose();
    } catch (err) {
      enqueueSnackbar(`Create failed: ${err instanceof Error ? err.message : err}`, {
        variant: 'error',
      });
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add app</DialogTitle>
      <DialogContent>
        <Stack spacing={2} className="pt-2">
          <TextField
            label="App name"
            size="small"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
          <Autocomplete
            freeSolo
            size="small"
            options={categories}
            value={category}
            onInputChange={(_e, v) => setCategory(v)}
            renderInput={(params) => (
              <TextField {...params} label="Category" helperText="Pick one, or type a new one" />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={createApp.isPending || !name.trim()}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Per-app users dialog ────────────────────────────────────────────
// "group:<owner>" is an internal sync detail — show just Group vs Direct
// (Corp IT feedback). Full value stays in the tooltip for debugging.
// Extract emails from a vendor user-export CSV/XLSX (e.g. Autodesk: one row
// per user × product — dedupe happens server-side too).
async function emailsFromFile(file: File): Promise<string[]> {
  const XLSX = await import('xlsx');
  const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json<(string | null)[]>(ws, {
    header: 1,
    raw: false,
    defval: null,
  });
  if (rows.length < 2) throw new Error('File has no data rows');
  const headers = (rows[0] ?? []).map((h) =>
    String(h ?? '')
      .trim()
      .toLowerCase(),
  );
  const emailIdx = headers.findIndex((h) => h.includes('email'));
  if (emailIdx === -1)
    throw new Error(`No email column found. Headers: ${headers.filter(Boolean).join(', ')}`);
  const emails = rows
    .slice(1)
    .map((r) =>
      String(r[emailIdx] ?? '')
        .trim()
        .toLowerCase(),
    )
    .filter((e) => e.includes('@'));
  return [...new Set(emails)];
}

function AppUsersDialog({ appName, onClose }: { appName: string; onClose: () => void }) {
  const { data, isLoading } = useAppUsers(appName);
  const bulkGrant = useBulkGrant();
  const { enqueueSnackbar } = useSnackbar();
  const fileInput = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    try {
      const emails = await emailsFromFile(file);
      if (emails.length === 0) throw new Error('No emails found in the file');
      const result = await bulkGrant.mutateAsync({ app_name: appName, emails });
      enqueueSnackbar(
        `Mapped ${result.granted} users to ${appName} (pinned — sync won't remove them)`,
        { variant: 'success' },
      );
    } catch (err) {
      enqueueSnackbar(`Upload failed: ${err instanceof Error ? err.message : err}`, {
        variant: 'error',
      });
    } finally {
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const renderRow = (u: AppUserRow) => (
    <TableRow key={u.email}>
      <TableCell>
        {[u.first_name, u.last_name].filter(Boolean).join(' ') || u.email}
        {!u.hr_active && (
          <Tooltip title="In Entra but not in the HR roster — possibly departed">
            <Chip size="small" color="warning" label="stale" className="ml-2" />
          </Tooltip>
        )}
      </TableCell>
      <TableCell>{u.email}</TableCell>
      <TableCell>{u.department ?? '—'}</TableCell>
      <TableCell>{u.job_title ?? '—'}</TableCell>
    </TableRow>
  );

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Who has access — {appName}</span>
          <div>
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              hidden
              ref={fileInput}
              onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={() => fileInput.current?.click()}
              disabled={bulkGrant.isPending}
            >
              {bulkGrant.isPending ? 'Mapping…' : 'Upload users CSV'}
            </Button>
          </div>
        </Stack>
      </DialogTitle>
      <DialogContent>
        {isLoading ? (
          <CircularProgress size={24} />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Title</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{(data ?? []).map(renderRow)}</TableBody>
          </Table>
        )}
        {(data ?? []).length === 0 && !isLoading && (
          <Typography color="text.secondary" className="py-4">
            No assignments recorded for this app.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
